import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { format, isAfter, isBefore } from '../../../utils/dateUtils';

const EventCard = ({ event }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const getEventStatusInfo = () => {
    // Use the eventStatus directly from the API response
    const status = event.eventStatus || 'Upcoming';
    
    switch (status) {
      case 'In Progress':
        return {
          status: 'In Progress',
          color: 'yellow',
          bgColor: 'bg-yellow-100',
          textColor: 'text-yellow-800',
          dotColor: 'bg-yellow-500'
        };
      case 'Completed':
        return {
          status: 'Completed',
          color: 'green',
          bgColor: 'bg-green-100',
          textColor: 'text-green-800',
          dotColor: 'bg-green-500'
        };
      case 'Upcoming':
      default:
        return {
          status: 'Upcoming',
          color: 'blue',
          bgColor: 'bg-blue-100',
          textColor: 'text-blue-800',
          dotColor: 'bg-blue-500'
        };
    }
  };

  const getEventTypeInfo = () => {
    switch (event.eventType) {
      case 'Match':
        return {
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          ),
          color: 'text-red-600',
          bgColor: 'bg-red-100'
        };
      case 'Training':
        return {
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 7.172V5L8 4z" />
            </svg>
          ),
          color: 'text-blue-600',
          bgColor: 'bg-blue-100'
        };
      default:
        return {
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2v12a2 2 0 002 2z" />
            </svg>
          ),
          color: 'text-purple-600',
          bgColor: 'bg-purple-100'
        };
    }
  };

  const getAttendanceStatusInfo = () => {
    const status = event.userAttendanceStatus;
    
    if (!status || status === 'pending') {
      return {
        status: 'No Response',
        color: 'text-gray-600',
        bgColor: 'bg-gray-100'
      };
    }

    switch (status) {
      case 'attended':
      case 'going':
      case 'coming':
        return {
          status: 'Attended',
          color: 'text-green-600',
          bgColor: 'bg-green-100'
        };
      case 'absent':
      case 'not_going':
      case 'notComing':
        return {
          status: 'Absent',
          color: 'text-red-600',
          bgColor: 'bg-red-100'
        };
      case 'maybe':
      case 'doNotKnow':
        return {
          status: 'Maybe',
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-100'
        };
      default:
        return {
          status: 'No Response',
          color: 'text-gray-600',
          bgColor: 'bg-gray-100'
        };
    }
  };

  // Get team count for display
  const getTeamCount = () => {
    if (event.eventType === 'Match' && event.teamInfo) {
      return 2;
    }
    return 1; // Single team events like Training
  };

  // Get participants count
  const getParticipantsCount = () => {
    return event.participantsCount || event.metaData?.totalMembers || event.members?.length || 0;
  };

  const statusInfo = getEventStatusInfo();
  const typeInfo = getEventTypeInfo();
  const attendanceInfo = getAttendanceStatusInfo();

  const handleViewEvent = () => {
    navigate(`/player/events/${event._id}`);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl hover:shadow-xl transition-all duration-300 overflow-hidden group hover:border-orange-200">
      {/* Event Header */}
      <div className="p-6 pb-4">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3 flex-1">
            <div className={`${typeInfo.bgColor} ${typeInfo.color} p-3 rounded-xl group-hover:scale-110 transition-transform duration-200`}>
              {typeInfo.icon}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-xl font-bold text-gray-900 line-clamp-1 group-hover:text-orange-600 transition-colors duration-200">
                {event.name}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {event.eventType} • {getParticipantsCount()} {t("common.participants")}
              </p>
            </div>
          </div>
          <div className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold ${statusInfo.bgColor} ${statusInfo.textColor} border border-current`}>
            <div className={`w-2 h-2 rounded-full mr-2 ${statusInfo.dotColor} animate-pulse`}></div>
            {statusInfo.status}
          </div>
        </div>

        {/* Event Description */}
        {event.description && (
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <p className="text-sm text-gray-700 line-clamp-2 leading-relaxed">
              {event.description}
            </p>
          </div>
        )}

        {/* Event Details */}
        <div className="space-y-4">
          {/* Date and Time */}
          <div className="flex items-center text-sm text-gray-600 bg-blue-50 rounded-lg p-3">
            <div className="bg-blue-100 p-2 rounded-lg mr-3">
              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <div className="font-medium text-gray-900">
                {format(new Date(event.startDate), 'MMM dd, yyyy')}
              </div>
              <div className="text-blue-700">
                {format(new Date(event.startDate), 'HH:mm')}
                {event.endDate && (
                  <span> - {format(new Date(event.endDate), 'HH:mm')}</span>
                )}
              </div>
            </div>
          </div>

          {/* Location */}
          {event.metaData?.location && (
            <div className="flex items-center text-sm text-gray-600 bg-green-50 rounded-lg p-3">
              <div className="bg-green-100 p-2 rounded-lg mr-3">
                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 616 0z" />
                </svg>
              </div>
              <span className="font-medium text-gray-900">{event.metaData.location}</span>
            </div>
          )}

          {/* Team Info (for single team events like Training) */}
          {event.eventType !== 'Match' && event.teamInfo && (
            <div className="flex items-center text-sm text-gray-600 bg-purple-50 rounded-lg p-3">
              <div className="bg-purple-100 p-2 rounded-lg mr-3">
                <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857M2 15a3 3 0 106 0M15 15a3 3 0 106 0M2 15V9a1 1 0 011-1h4a1 1 0 011 1v6M15 15V9a1 1 0 011-1h4a1 1 0 011 1v6" />
                </svg>
              </div>
              <div className="flex items-center space-x-3">
                {event.teamInfo.logo && (
                  <img
                    src={event.teamInfo.logo}
                    alt={event.teamInfo.name}
                    className="w-8 h-8 rounded-full object-cover border-2 border-white shadow-md"
                  />
                )}
                <div>
                  <div className="font-semibold text-gray-900">{event.teamInfo.name}</div>
                  <div className="text-xs px-2 py-1 bg-purple-200 text-purple-700 rounded-full inline-block mt-1">
                    {event.teamInfo.sport}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Teams (for matches) - Updated to use new API structure */}
          {event.eventType === 'Match' && event.teamInfo && (
            <div className="bg-gradient-to-r from-red-50 to-blue-50 rounded-xl p-4">
              <div className="flex items-center justify-between">
                {/* Team 1 */}
                <div className="flex items-center space-x-3 flex-1">
                  {event.teamInfo.team1?.logo && (
                    <img
                      src={event.teamInfo.team1.logo}
                      alt={event.teamInfo.team1.name}
                      className="w-10 h-10 rounded-full object-cover border-3 border-red-200 shadow-lg"
                    />
                  )}
                  <div>
                    <div className="text-sm font-bold text-gray-900">
                      {event.teamInfo.team1?.name || 'Team 1'}
                    </div>
                    {event.metaData?.score?.team1 !== undefined && (
                      <div className="text-2xl font-black text-red-600">
                        {event.metaData.score.team1}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* VS Divider */}
                <div className="bg-white shadow-lg rounded-full px-4 py-2 mx-4">
                  <span className="text-sm font-black text-gray-700">VS</span>
                </div>
                
                {/* Team 2 */}
                <div className="flex items-center space-x-3 flex-1 justify-end text-right">
                  <div>
                    <div className="text-sm font-bold text-gray-900">
                      {event.teamInfo.team2?.name || 'Team 2'}
                    </div>
                    {event.metaData?.score?.team2 !== undefined && (
                      <div className="text-2xl font-black text-blue-600">
                        {event.metaData.score.team2}
                      </div>
                    )}
                  </div>
                  {event.teamInfo.team2?.logo && (
                    <img
                      src={event.teamInfo.team2.logo}
                      alt={event.teamInfo.team2.name}
                      className="w-10 h-10 rounded-full object-cover border-3 border-blue-200 shadow-lg"
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Event Footer */}
      <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-orange-50 border-t border-gray-100">
        <div className="flex items-center justify-between">
          {/* Attendance Status */}
          <div className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold border ${attendanceInfo.bgColor} ${attendanceInfo.color} border-current`}>
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            {attendanceInfo.status}
          </div>

          {/* View Event Button */}
          <button
            onClick={handleViewEvent}
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white text-sm font-semibold rounded-xl transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
          >
            {t("common.viewDetails")}
            <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default EventCard; 